import { highlightRuns, textRuns } from '@/lib/session-search';
import type { SearchSpan } from '@matter_extractor/shared';
import { Fragment } from 'react';

/**
 * A text with the passages of the search underlined (`recherche-sessions.md` §5): the
 * `<mark>` of the design system - a transparent background, weight 700, `bleu-nuit`.
 *
 * Two ways to say where: `terms`, the folded words of `parseSearchQuery`, for a column
 * the line already shows (the name of the person, of the experience...), cut by
 * `findTermSpans` - the function the server retained the session with - and `spans`,
 * the passages the server served for an excerpt. Without either it is the text, plain:
 * a list that is not searched renders exactly what it rendered before.
 */

export interface MarkedTextProps {
  readonly text: string;
  readonly terms?: readonly string[] | null | undefined;
  readonly spans?: readonly SearchSpan[] | undefined;
}

export const MARK_CLASS = 'bg-transparent font-bold text-bleu-nuit';

export function MarkedText({ text, terms = null, spans }: MarkedTextProps) {
  const runs = spans === undefined ? highlightRuns(text, terms) : textRuns(text, spans);
  if (runs.length === 1 && !runs[0]?.marked) return <>{text}</>;
  return (
    <>
      {runs.map((run, at) => (
        // The runs are a pure cut of one string: their position is their identity.
        // biome-ignore lint/suspicious/noArrayIndexKey: a cut of a string has no other key
        <Fragment key={at}>
          {run.marked ? <mark className={MARK_CLASS}>{run.text}</mark> : run.text}
        </Fragment>
      ))}
    </>
  );
}
